/**
 * Role-aware navigation rail.
 *
 * Item visibility has three axes (task 97 §2.1, task 98 §2.2):
 *   - `roles`             — базовый ролевой тир (admin/office_staff/viewer/…);
 *   - `requiresLead`      — деривативный флаг «ведёт юнит» из орг-дерева
 *                           (`led_unit_ids ≠ ∅`), а НЕ роль. Портфельные пункты
 *                           (проекты, риски) — `visible_unit_ids`-семантика,
 *                           которую зам НЕ получает;
 *   - `requiresActsOnUnit`— «действует на юнит» = ведёт ИЛИ активно замещает
 *                           (`acting_unit_ids ≠ ∅`, task 98). Acting-пункты
 *                           (подтверждение времени, «Сотрудники») открываются
 *                           и заму, не только лиду.
 * Пункт виден, если проходит по роли ЛИБО (`requiresLead` && лид) ЛИБО
 * (`requiresActsOnUnit` && действует на юнит).
 */
import { useTranslation } from "react-i18next";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  Briefcase,
  Clock,
  ClipboardList,
  ClipboardCheck,
  FileText,
  Library,
  ListChecks,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldAlert,
  Users,
} from "lucide-react";
import {
  useSession,
  useIsLead,
  useActsOnUnit,
  useTracksTime,
  isProjectManager,
  type UserRole,
} from "@/store/session";
import { useUiPrefs } from "@/store/uiPrefs";
import { FEATURES } from "@/config/featureFlags";
import { usePeopleCapabilities } from "@/hooks/usePeopleCapabilities";
import { PHONE_QUERY, useIsNarrowViewport } from "@/hooks/useIsNarrowViewport";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { LucideIcon } from "lucide-react";

/**
 * Sectioned rail: a flat `NavItem[]` carries a `section` tag; the render groups
 * by section and prints a quiet caption + divider per group (NOT a collapsible
 * two-level tree). Section order = render order below. See task 82 §2.1 and
 * `docs/frontend/patterns.md §Секционная навигация`.
 */
type NavSection = "myWork" | "decisions" | "reference";

const SECTION_ORDER: NavSection[] = ["myWork", "decisions", "reference"];

/**
 * Видимость секции рейла (задача 100 §1.2). Секция «Решения» скрыта общим
 * фича-флагом `FEATURES.decisions` (`config/featureFlags.ts`) — её пункты
 * **остаются** в `NAV`, но не рендерятся; восстановление = флип флага на `true`.
 * Скрытие только из меню: route-гейтов не добавляет, по прямому URL страницы
 * пунктов всё ещё открываются. Остальные секции видны всегда. См.
 * `docs/frontend/patterns.md §Секционная навигация` / `§Фича-флаги видимости`.
 */
function isSectionVisible(section: NavSection): boolean {
  if (section === "decisions") return FEATURES.decisions;
  return true;
}

const sectionLabels: Record<NavSection, string> = {
  myWork: "nav.section.myWork",
  decisions: "nav.section.decisions",
  reference: "nav.section.reference",
};

interface NavItem {
  key: keyof typeof navLabels;
  section: NavSection;
  to: string;
  search?: Record<string, string>;
  icon: LucideIcon;
  roles: UserRole[];
  /** Also show for a lead (leads at least one unit), regardless of base role. */
  requiresLead?: boolean;
  /**
   * Also show for anyone who *acts on* a unit — a lead or an active deputy
   * (`acting_unit_ids ≠ ∅`, task 98 §2.2). Wider than `requiresLead`: a plain
   * deputy has no `led_unit_ids` but reaches the acting-scoped consoles.
   */
  requiresActsOnUnit?: boolean;
  /**
   * Also show for anyone who *tracks time* (`tracks_time`, task 110). Drives
   * the self-entry visibility of «Учёт времени»: a plain office user with the
   * flag on reaches the self-editor even without a console, while a toggled-off
   * user (any role) does not. Combined with `roles`/`requiresActsOnUnit` this
   * keeps the item visible to a console lead whose own `tracks_time` is off.
   */
  requiresTracksTime?: boolean;
  requiresPeopleRead?: boolean;
  requiresProjectManager?: boolean;
}

const navLabels = {
  projects: "nav.projects",
  approvals: "nav.approvals",
  risks: "nav.risks",
  portfolio: "nav.portfolio",
  load: "nav.load",
  people: "nav.users",
  templates: "nav.templates",
  audit: "nav.audit",
  catalogs: "nav.catalogs",
  timeApproval: "nav.time-approval",
  workStatus: "nav.work-status",
} as const;

export const NAV: NavItem[] = [
  // Моя работа
  {
    key: "projects",
    section: "myWork",
    to: "/projects",
    icon: Briefcase,
    roles: ["admin", "viewer"],
    requiresProjectManager: true,
    requiresLead: true,
  },
  {
    // «Учёт времени» — двойного назначения: консоль подтверждения (admin/лид/зам)
    // ИЛИ self-редактор (для тех, кто участвует в учёте). Видимость =
    // консоль ∪ участвует в учёте (task 110 §2.1): admin — по роли, лид/зам —
    // по «действует на юнит», рядовой участник — по `tracks_time`. Toggled-off
    // сотрудник любой роли (в т.ч. plain pmo/office_staff) пункт не видит.
    key: "timeApproval",
    section: "myWork",
    to: "/time-approval",
    icon: Clock,
    roles: ["admin"],
    requiresActsOnUnit: true,
    requiresTracksTime: true,
  },
  {
    // «Статусы работ» — соседний с подтверждением времени пункт того же раздела
    // (задача 114 §2.1). Экран сугубо руководительский: у исполнителя в этой
    // задаче не появляется ничего, поэтому `requiresTracksTime` тут НЕТ —
    // видимость = admin (по роли) ∪ «действует на юнит» (лид/зам из дерева).
    key: "workStatus",
    section: "myWork",
    to: "/work-status",
    icon: ClipboardCheck,
    roles: ["admin"],
    requiresActsOnUnit: true,
  },
  // Решения
  {
    key: "portfolio",
    section: "decisions",
    to: "/portfolio",
    icon: Activity,
    roles: ["admin", "viewer"],
    requiresProjectManager: true,
  },
  {
    key: "load",
    section: "decisions",
    to: "/load",
    icon: ListChecks,
    roles: ["admin"],
    requiresProjectManager: true,
  },
  {
    key: "risks",
    section: "decisions",
    to: "/risks",
    icon: ShieldAlert,
    roles: ["admin"],
    requiresProjectManager: true,
    requiresLead: true,
  },
  {
    key: "approvals",
    section: "decisions",
    to: "/approvals",
    icon: ClipboardList,
    roles: ["admin"],
    requiresProjectManager: true,
  },
  // Справочники / настройка
  {
    key: "people",
    section: "reference",
    to: "/people",
    search: { tab: "employees" },
    icon: Users,
    roles: [],
    requiresPeopleRead: true,
  },
  {
    key: "catalogs",
    section: "reference",
    to: "/admin/catalogs",
    icon: Library,
    roles: ["admin"],
    requiresProjectManager: true,
  },
  {
    key: "templates",
    section: "reference",
    to: "/templates",
    icon: FileText,
    roles: ["admin"],
    requiresProjectManager: true,
  },
  {
    key: "audit",
    section: "reference",
    to: "/audit",
    icon: ClipboardList,
    roles: ["admin"],
    requiresProjectManager: true,
  },
];

export function LeftRail(): React.ReactElement {
  const { t } = useTranslation();
  const role = useSession((s) => s.user?.role);
  const isLead = useIsLead();
  const actsOnUnit = useActsOnUnit();
  const tracksTime = useTracksTime();
  const projectManager = useSession((s) => isProjectManager(s.user));
  const canReadPeople = usePeopleCapabilities().data?.can_read ?? false;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const savedCollapsed = useUiPrefs((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiPrefs((s) => s.toggleSidebar);
  const phone = useIsNarrowViewport(PHONE_QUERY);
  const collapsed = phone || savedCollapsed;
  if (!role) return <aside data-testid="leftrail" />;
  const visible = NAV.filter(
    (n) =>
      n.roles.includes(role) ||
      (n.requiresLead === true && isLead) ||
      (n.requiresActsOnUnit === true && actsOnUnit) ||
      (n.requiresTracksTime === true && tracksTime) ||
      (n.requiresProjectManager === true && projectManager) ||
      (n.requiresPeopleRead === true && canReadPeople),
  );
  const sections = SECTION_ORDER.filter(isSectionVisible)
    .map((section) => ({
      section,
      items: visible.filter((n) => n.section === section),
    }))
    .filter((g) => g.items.length > 0);

  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggleLabel = t(collapsed ? "nav.expand" : "nav.collapse");

  const renderItem = (item: NavItem): React.ReactElement => {
    const Icon = item.icon;
    const isActive =
      pathname === item.to || (item.to !== "/" && pathname.startsWith(`${item.to}/`));
    const label = t(navLabels[item.key]);
    const search =
      item.key === "catalogs" ? { tab: role === "admin" ? "positions" : "calendar" } : item.search;
    const link = (
      <Link
        key={item.key}
        to={item.to}
        search={search}
        data-testid={`nav-${item.key}`}
        aria-label={collapsed ? label : undefined}
        className={[
          "flex items-center rounded-[2px] text-[14px]",
          collapsed ? "justify-center p-[8px]" : "gap-[8px] px-[8px] py-[6px]",
          isActive ? "bg-secondary font-medium" : "hover:bg-accent",
        ].join(" ")}
      >
        <Icon className="h-[16px] w-[16px] shrink-0" />
        {!collapsed && label}
      </Link>
    );
    if (collapsed) {
      return (
        <Tooltip key={item.key}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      );
    }
    return link;
  };

  return (
    <aside
      data-testid="leftrail"
      data-collapsed={collapsed ? "true" : "false"}
      className={[
        "border-border flex shrink-0 flex-col gap-[4px] border-r bg-[var(--bg-panel-0)] p-[8px]",
        collapsed ? "w-[56px]" : "w-[224px]",
      ].join(" ")}
      aria-label="primary"
    >
      {!phone && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid="leftrail-toggle"
              aria-label={toggleLabel}
              onClick={toggleSidebar}
              className={[
                "hover:bg-accent text-muted-foreground flex items-center rounded-[2px]",
                collapsed ? "justify-center p-[8px]" : "gap-[8px] px-[8px] py-[6px]",
              ].join(" ")}
            >
              <ToggleIcon className="h-[16px] w-[16px] shrink-0" />
              {!collapsed && <span className="text-[12px]">{toggleLabel}</span>}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{toggleLabel}</TooltipContent>
        </Tooltip>
      )}
      {sections.map((group, i) => (
        <div
          key={group.section}
          data-testid={`nav-section-${group.section}`}
          role="group"
          aria-label={t(sectionLabels[group.section])}
          className="flex flex-col gap-[4px]"
        >
          {collapsed ? (
            // Свёрнутый рейл: 1px-разделитель перед КАЖДОЙ группой, включая первую
            // (отделяет её от тумблера) — зеркалит легенду-подпись развёрнутого вида
            // (задача 100 §1.1). Отступы абсолютные px по дизайн-хендоффу; целые px
            // → линия ложится на целый девайс-пиксель, без AA-«2px».
            <div
              aria-hidden="true"
              className={["mx-[8px] h-px", i > 0 ? "mt-[10px] mb-[4px]" : "mt-[6px] mb-[2px]"].join(
                " ",
              )}
              style={{ background: "var(--rule-line)" }}
            />
          ) : (
            // Метка-легенда на линии (задача 100 §1.1, вариант 1C «метка на линии»):
            // тихая подпись слева + хайрлайн --rule-line до правого края, чтобы
            // группировка читалась как аннотация-легенда, а не кликабельный пункт.
            // Некликабельна, без hover/focus. Отступы абсолютные px (по дизайн-
            // хендоффу): у не-первой группы больше воздуха сверху; целые px → линия
            // чёткая 1px. См. `patterns.md §Секционная навигация`.
            <div
              className={[
                "flex items-center gap-[8px] px-[8px] pb-[4px] select-none",
                i > 0 ? "pt-[16px]" : "pt-[12px]",
              ].join(" ")}
            >
              <span className="font-condensed text-[11px] font-medium tracking-[0.08em] whitespace-nowrap uppercase text-[color:var(--fg-4)]">
                {t(sectionLabels[group.section])}
              </span>
              <span
                aria-hidden="true"
                className="h-px min-w-0 flex-1"
                style={{ background: "var(--rule-line)" }}
              />
            </div>
          )}
          {group.items.map(renderItem)}
        </div>
      ))}
    </aside>
  );
}
